import type {
  Id,
  AnswerId,
  ConditionValue,
  Graph,
  EditableGraph,
  ConversationNode,
  EditableNodeType,
  ConditionOperator,
} from 'src/types/chatbot';

import type {Brand} from 'src/types/brands';

import without from 'lodash/without';
import invariant from 'invariant';

import {
  getRoot,
  getChildEdges,
  getParentEdges,
  initEditableGraph,
  createEdge,
  getAllChildNodes,
} from './utils';
import {fromArray} from 'src/utils/map';


export type PresetQuestion = {
  intent: string;
  nlu_entity_type: string;
  score: number;
};

export type ConversationBuilderState = {
  history: Array<EditableGraph>;
  historyIndex: number;
  insertingNode:
    | {
        nodeType: EditableNodeType;
        edgeId: Id;
        question_type?: 'preset' | 'custom';
      }
    | null
    | undefined;
  editingNode: Id | null | undefined;
  jumpingFrom:
    | {
        edgeId: Id;
      }
    | null
    | undefined;
  editingEdge: Id | null | undefined;
  convertingToPreset: boolean;
  hideQuestionSuggestions: Id[];
  brands: Brand[] | null | undefined;
  provisionedPhoneNumbers: string[] | null | undefined;
  provisionedWhatsappPhoneNumbers: string[] | null | undefined;
  keywords:
    | {
        [key: string]: number;
      }
    | null
    | undefined;
};

// TODO (kyle): remove these boilerplate constants
const EDIT_NODE = 'EDIT_NODE';
const CANCEL_INSERT = 'CANCEL_INSERT';

type ConversationBuilderAction =
  | {
      type: 'UNDO';
    }
  | {
      type: 'REDO';
    }
  | {
      type: 'RESET_STATE';
      payload: Graph;
    }
  | {
      type: 'CONVERT_TO_PRESET';
      payload: {
        nodeId: Id;
      };
    }
  | {
      type: 'HIDE_QUESTION_SUGGESTION';
      payload: Id[];
    }
  | {
      type: 'EDIT_NODE';
      payload: Id;
    }
  | {
      type: 'UPDATE_NODE';
      payload: ConversationNode;
    }
  | {
      type: 'COMMIT_NODE';
      payload: ConversationNode;
    }
  | {
      type: 'INSERT_NODE';
      payload: {
        node: ConversationNode;
        edgeId: Id;
      };
    }
  | {
      type: 'CANCEL_INSERT';
    }
  | {
      type: 'NEW_NODE';
      payload: {
        nodeType: EditableNodeType;
        edgeId: Id;
        question_type?: 'preset' | 'custom';
      };
    }
  | {
      type: 'CANCEL_EDIT_NODE';
    }
  | {
      type: 'REMOVE_NODE';
      payload: Id;
    }
  | {
      type: 'ADD_BRANCH';
      payload: {
        nodeId: Id;
      };
    }
  | {
      type: 'REMOVE_BRANCH';
      payload: {
        edgeId: Id;
      };
    }
  | {
      type: 'REMOVE_CONDITION';
      payload: {
        edgeId: Id;
        answerId: ConditionValue;
      };
    }
  | {
      type: 'ADD_CONDITION';
      payload: {
        edgeId: Id;
        answerId: AnswerId;
      };
    }
  | {
      type: 'START_JUMP';
      payload: {
        edgeId: Id;
      };
    }
  | {
      type: 'CANCEL_JUMP';
    }
  | {
      type: 'ADD_JUMP';
      payload: {
        nodeId: Id;
      };
    }
  | {
      type: 'EDIT_EDGE';
      payload: Id;
    }
  | {
      type: 'CANCEL_EDIT_EDGE';
    }
  | {
      type: 'SUBMIT_EDIT_EDGE';
      payload: {
        conditions: ConditionValue[];
        operator: ConditionOperator | null;
      };
    }
  | {
      type: 'TRIM_EDGE';
      payload: {
        edgeId: Id;
      };
    }
  | {
      type: 'DONE_SIZING';
      payload: Map<Id, number>;
    }
  | {
      type: 'COMMIT_GRAPH';
      payload: EditableGraph;
    }
  | {
      type: 'RECEIVE_DATA';
      key: string;
      payload:
        | Brand[]
        | string[]
        | {
            [key: string]: number;
          };
    };

export type ConversationDispatch = (arg1: ConversationBuilderAction) => void;

export function reducer(
  state: ConversationBuilderState,
  action: ConversationBuilderAction,
): ConversationBuilderState {
  switch (action.type) {
    case 'UNDO':
      return {...state, historyIndex: Math.max(state.historyIndex - 1, 0)};
    case 'REDO':
      return {
        ...state,
        historyIndex: Math.min(
          state.historyIndex + 1,
          state.history.length - 1,
        ),
      };
    case 'RESET_STATE':
      return initState(action.payload);
    case 'EDIT_NODE':
      return {...state, editingNode: action.payload};
    case 'CONVERT_TO_PRESET':
      return {
        ...state,
        editingNode: action.payload.nodeId,
        convertingToPreset: true,
      };
    case 'HIDE_QUESTION_SUGGESTION':
      return {
        ...state,
        hideQuestionSuggestions: action.payload,
      };
    case 'CANCEL_EDIT_NODE':
      return {...state, editingNode: null, convertingToPreset: false};
    case 'UPDATE_NODE': {
      const updatedNode = action.payload;

      const graph = getMutableGraph(state);

      const oldNode = graph.nodes.get(updatedNode.id);
      graph.nodes.set(updatedNode.id, updatedNode);

      // NOTE (kyle): for question nodes we have to handle
      // changes that affect their child edges.
      if (oldNode) {
        // NOTE: on updating Question node name, nodes that use it need to be updated
        // Nodes to be updated are: Summary
        if (oldNode.type === 'question' && oldNode.name !== updatedNode.name) {
          const childNodes = getAllChildNodes(graph, updatedNode.id);
          for (const childNode of childNodes) {
            // TODO: add text_draft_json edit snippet for other type of nodes
            if (childNode.type === 'summary') {
              const updatedChildNode = {
                ...childNode,
                summary_items: childNode.summary_items.map((item) => {
                  return item.node_id === updatedNode.id
                    ? {
                        ...item,
                        short_name: updatedNode.name,
                      }
                    : item;
                }),
              } as const;

              graph.nodes.set(updatedChildNode.id, updatedChildNode);
            }
          }
        }

        // NOTE (kyle): remove operators if node is not multiselect
        if (
          // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConditionNode | DispositionNode | DynamicQuestionNode | EndConversationNode | FileUploadNode | ... 8 more ... | TextToApplyNode'.
          oldNode.question_is_multiselect &&
          // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'.
          !updatedNode.question_is_multiselect
        ) {
          for (const edge of getChildEdges(graph, oldNode.id)) {
            graph.edges.set(edge.id, {...edge, operator: null});
          }
        }
        // NOTE (kyle): add default operators for multiselect node
        if (
          // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConditionNode | DispositionNode | DynamicQuestionNode | EndConversationNode | FileUploadNode | ... 8 more ... | TextToApplyNode'.
          !oldNode.question_is_multiselect &&
          // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'.
          updatedNode.question_is_multiselect
        ) {
          for (const edge of getChildEdges(graph, oldNode.id)) {
            if (edge.conditions) {
              graph.edges.set(edge.id, {...edge, operator: 'every'});
            }
          }
        }
        // NOTE (kyle): remove branches if node no longer has answers
        // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConditionNode | DispositionNode | DynamicQuestionNode | EndConversationNode | FileUploadNode | ... 8 more ... | TextToApplyNode'. | TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
        if (oldNode.answers && !updatedNode.answers) {
          for (const edge of getChildEdges(graph, oldNode.id)) {
            if (edge.conditions) {
              graph.edges.delete(edge.id);
            }
          }
        }
        // NOTE (kyle): remove all irrelevant conditions
        // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConditionNode | DispositionNode | DynamicQuestionNode | EndConversationNode | FileUploadNode | ... 8 more ... | TextToApplyNode'. | TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
        if (oldNode.answers && updatedNode.answers) {
          // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'. | TS7031 - Binding element 'id' implicitly has an 'any' type.
          const answerIds = new Set(updatedNode.answers.map(({id}) => id));
          for (const edge of getChildEdges(graph, oldNode.id)) {
            if (edge.conditions) {
              edge.conditions = edge.conditions.filter((condition) =>
                answerIds.has(condition),
              );
            }
          }

          const childEdges = getChildEdges(graph, oldNode.id);
          if (
            // @ts-expect-error - TS2339 - Property 'preset_question_id' does not exist on type 'ConditionNode | DispositionNode | DynamicQuestionNode | EndConversationNode | FileUploadNode | ... 8 more ... | TextToApplyNode'.
            !oldNode.preset_question_id &&
            // @ts-expect-error - TS2339 - Property 'preset_question_id' does not exist on type 'ConversationNode'.
            updatedNode.preset_question_id &&
            childEdges.length > 1
          ) {
            // if converting from a custom question with branches to a preset question, add empty branch with all options in this condition
            // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'.
            const question_is_multiselect = updatedNode.question_is_multiselect;

            const newEdge = createEdge(
              updatedNode.id,
              null,
              // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'. | TS7031 - Binding element 'id' implicitly has an 'any' type.
              updatedNode.answers?.map(({id}) => id) ?? [], //should not need ?. because of earlier if statement, but flow demands
              question_is_multiselect ? 'every' : null,
            );
            graph.edges.set(newEdge.id, newEdge);
          }
        }
      }

      return reduceUpdatedState(state, graph, {
        editingNode: null,
        convertingToPreset: false,
      });
    }

    case 'NEW_NODE':
      return {...state, insertingNode: action.payload};
    case 'CANCEL_INSERT':
      return {...state, insertingNode: null};
    case 'COMMIT_NODE': {
      const newNode = action.payload;
      if (!state.insertingNode) {
        return state;
      }
      const {edgeId} = state.insertingNode;
      return insertNode(state, newNode, edgeId);
    }
    case 'INSERT_NODE': {
      const {node, edgeId} = action.payload;
      return insertNode(state, node, edgeId);
    }
    case 'REMOVE_NODE': {
      let graph = getMutableGraph(state);
      const nodeId = action.payload;

      const childEdges = getChildEdges(graph, nodeId);

      // if there is a single child, we splice the tree,
      // otherwise we prune it.
      const newChildNodeId =
        childEdges.length === 1 ? childEdges[0].to_node_id : null;

      const parentEdges = getParentEdges(graph, nodeId);
      for (let edge of parentEdges) {
        edge = {
          ...edge,
          to_node_id: newChildNodeId,
        };
        graph.edges.set(edge.id, edge);

        if (edge.id === graph.root) {
          let newRootNode = newChildNodeId && graph.nodes.get(newChildNodeId);
          if (newRootNode) {
            newRootNode = clone(newRootNode, {is_root: true});
            graph.nodes.set(newRootNode.id, newRootNode);
          }
        }
      }

      graph = pruneGraph(graph);

      // remove referencing summary node items
      for (const node of [...graph.nodes.values()]) {
        if (node.type === 'summary') {
          const summary_items = node.summary_items.filter(
            (item) => item.node_id !== nodeId,
          );
          if (summary_items.length !== node.summary_items.length) {
            graph.nodes.set(node.id, {
              ...node,
              summary_items,
            });
          }
        }
      }

      return reduceUpdatedState(state, graph);
    }

    case 'ADD_BRANCH': {
      const graph = getMutableGraph(state);
      const {nodeId} = action.payload;

      const node = graph.nodes.get(nodeId);
      const question_is_multiselect =
        // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'. | TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'.
        node?.question_is_multiselect && node.question_is_multiselect;

      const newEdge = createEdge(
        nodeId,
        null,
        [],
        question_is_multiselect ? 'every' : null,
      );
      graph.edges.set(newEdge.id, newEdge);

      return reduceUpdatedState(state, graph);
    }

    case 'REMOVE_BRANCH': {
      const {edgeId} = action.payload;

      let graph = getMutableGraph(state);
      graph.edges.delete(edgeId);

      graph = pruneGraph(graph);

      return reduceUpdatedState(state, graph);
    }

    case 'ADD_CONDITION': {
      const graph = getMutableGraph(state);
      const {edgeId, answerId} = action.payload;

      let edge = graph.edges.get(edgeId);
      invariant(edge, 'Edge does not exist');

      edge = {
        ...edge,
        conditions: [...(edge.conditions || []), answerId],
      };
      graph.edges.set(edge.id, edge);

      return reduceUpdatedState(state, graph);
    }

    case 'REMOVE_CONDITION': {
      const graph = getMutableGraph(state);
      const {edgeId, answerId} = action.payload;

      let edge = graph.edges.get(edgeId);
      invariant(edge, 'Edge does not exist');

      edge = {
        ...edge,
        conditions: without(edge.conditions, answerId),
      };
      graph.edges.set(edge.id, edge);

      return reduceUpdatedState(state, graph);
    }

    case 'START_JUMP': {
      return {...state, jumpingFrom: action.payload};
    }
    case 'CANCEL_JUMP': {
      return {...state, jumpingFrom: null};
    }
    case 'ADD_JUMP': {
      let graph = getMutableGraph(state);

      invariant(state.jumpingFrom, 'Cannot complete jump.');

      const {edgeId} = state.jumpingFrom;
      const {nodeId} = action.payload;

      let edge = graph.edges.get(edgeId);
      invariant(edge, 'Edge does not exist.');

      edge = {
        ...edge,
        to_node_id: nodeId,
      };
      graph.edges.set(edge.id, edge);

      graph = pruneGraph(graph);

      return reduceUpdatedState(state, graph, {jumpingFrom: null});
    }

    case 'EDIT_EDGE': {
      return {
        ...state,
        editingEdge: action.payload,
      };
    }
    case 'CANCEL_EDIT_EDGE': {
      return {
        ...state,
        editingEdge: null,
      };
    }
    case 'SUBMIT_EDIT_EDGE': {
      const graph = getMutableGraph(state);

      invariant(state.editingEdge, 'No edge to submit.');

      let edge = graph.edges.get(state.editingEdge);

      invariant(edge, 'No edge to submit.');

      edge = {
        ...edge,
        ...action.payload,
      };
      graph.edges.set(edge.id, edge);

      return reduceUpdatedState(state, graph, {editingEdge: null});
    }
    case 'TRIM_EDGE': {
      let graph = getMutableGraph(state);

      let edge = graph.edges.get(action.payload.edgeId);
      invariant(edge, 'No edge to trim.');

      edge = {
        ...edge,
        to_node_id: null,
      };
      graph.edges.set(edge.id, edge);

      graph = pruneGraph(graph);

      return reduceUpdatedState(state, graph, {editingEdge: null});
    }

    // pushes a graph onto the history stack
    case 'COMMIT_GRAPH': {
      const graph = action.payload;
      return reduceUpdatedState(state, graph);
    }
    case 'RECEIVE_DATA': {
      return {
        ...state,
        [action.key]: action.payload,
      };
    }
  }
  throw new Error();
}

export function initState(graph: Graph): ConversationBuilderState {
  return {
    brands: null,
    provisionedPhoneNumbers: null,
    provisionedWhatsappPhoneNumbers: null,
    keywords: null,
    history: [initEditableGraph(graph)],
    historyIndex: 0,

    editingNode: null,
    insertingNode: null,
    jumpingFrom: null,
    editingEdge: null,
    convertingToPreset: false,
    // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'number[]'.
    hideQuestionSuggestions:
      window.localStorage
        ?.getItem('chatbotHideQuestionSuggestions')
        ?.split(',') ?? [],
  };
}

export function getMutableGraph(
  state: ConversationBuilderState,
): EditableGraph {
  const graph = state.history[state.historyIndex];
  return {
    ...graph,
    // @ts-expect-error - TS2322 - Type 'Map<number, ConversationNode>' is not assignable to type 'ConversationNode[] & Map<number, ConversationNode>'.
    nodes: new Map(graph.nodes),
    // @ts-expect-error - TS2322 - Type 'Map<number, Edge>' is not assignable to type 'Edge[] & Map<number, Edge>'.
    edges: new Map(graph.edges),
  };
}

function reduceUpdatedState(
  state: ConversationBuilderState,
  graph: EditableGraph,
  updates?: Partial<ConversationBuilderState>,
): ConversationBuilderState {
  const history = state.history.slice(0, state.historyIndex + 1);
  history.push(graph);
  return {
    ...state,
    ...updates,
    history,
    historyIndex: history.length - 1,
  };
}

function insertNode(
  state: ConversationBuilderState,
  node: ConversationNode,
  edgeId: Id,
): ConversationBuilderState {
  const graph = getMutableGraph(state);

  let edge = graph.edges.get(edgeId);
  invariant(edge, 'Cannot insert node at nonexistent edge.');

  let newNode = node;

  // Set this node as root and change previous root.
  if (edge.id === graph.root) {
    newNode = clone(newNode, {is_root: true});
    let toNode = edge.to_node_id != null && graph.nodes.get(edge.to_node_id);
    if (toNode && toNode.is_root) {
      toNode = clone(toNode, {is_root: false});
      graph.nodes.set(toNode.id, toNode);
    }
  }

  if (newNode.type === 'condition') {
    const ifEdge = createEdge(newNode.id, edge.to_node_id, null);
    ifEdge.conditions = [true];
    graph.edges.set(ifEdge.id, ifEdge);

    const elseEdge = createEdge(newNode.id, edge.to_node_id, null);
    elseEdge.conditions = [false];
    graph.edges.set(elseEdge.id, elseEdge);
  } else {
    const newEdge = createEdge(newNode.id, edge.to_node_id, null);
    graph.edges.set(newEdge.id, newEdge);
  }

  edge = {
    ...edge,
    to_node_id: newNode.id,
  };
  graph.edges.set(edge.id, edge);
  graph.nodes.set(newNode.id, newNode);

  return reduceUpdatedState(state, graph, {
    insertingNode: null,
  });
}

// TODO (kyle): this is not very performant because we have to do a full
// sweep of the tree any time we modify a node.
function pruneOrphans(graph: EditableGraph): EditableGraph {
  const reachableNodes = new Set();
  const queue = [graph.root];
  let current;

  while ((current = queue.shift())) {
    if (!reachableNodes.has(current)) {
      reachableNodes.add(current);
      const childEdges = getChildEdges(graph, current);
      if (childEdges.length > 0) {
        // @ts-expect-error - TS2345 - Argument of type 'number | null | undefined' is not assignable to parameter of type 'number'.
        queue.push(...childEdges.map((child) => child.to_node_id));
      }
    }
  }

  return {
    ...graph,
    // @ts-expect-error - TS2322 - Type 'Map<number, ConversationNode>' is not assignable to type 'ConversationNode[] & Map<number, ConversationNode>'.
    nodes: new Map(
      graph.nodes
        .entries()
        // @ts-expect-error - TS2769 - No overload matches this call.
        .filter(([nodeId]: [any]) => reachableNodes.has(nodeId)),
    ),
    // @ts-expect-error - TS2322 - Type 'Map<number, ConversationNode>' is not assignable to type 'Edge[] & Map<number, Edge>'.
    edges: new Map(
      graph.nodes
        .entries()
        .filter(([_, edge]: [any, any]) => reachableNodes.has(edge.to_node_id)),
    ),
  };
}

export function pruneGraph(graph: EditableGraph): EditableGraph {
  const rootEdge = getRoot(graph);

  const reachableEdges = new Set();
  const reachableNodes = new Set();

  const queue = [rootEdge];
  let currentEdge;

  // find reachable nodes and edges
  while ((currentEdge = queue.shift())) {
    reachableEdges.add(currentEdge);

    const {to_node_id} = currentEdge;
    if (to_node_id) {
      const childNode = graph.nodes.get(to_node_id);
      if (childNode) {
        reachableNodes.add(childNode);
      }

      queue.push(
        ...getChildEdges(graph, to_node_id).filter(
          (edge) => !reachableEdges.has(edge),
        ),
      );
    }
  }

  // remove any conditional node rules that reference
  // unreachable nodes.
  // @ts-expect-error - TS2345 - Argument of type 'Set<unknown>' is not assignable to parameter of type 'Iterable<{ id: unknown; }>'.
  const nodes = fromArray(reachableNodes);
  for (const node of reachableNodes) {
    // @ts-expect-error - TS18046 - 'node' is of type 'unknown'.
    if (node.type === 'condition') {
      const {
        // @ts-expect-error - TS2339 - Property 'condition_ruleset' does not exist on type 'unknown'.
        condition_ruleset: {expressions},
      } = node;
      const filteredExpressions = expressions.filter(
        // @ts-expect-error - TS7006 - Parameter 'expression' implicitly has an 'any' type.
        (expression) =>
          nodes.has(expression.node_id_answer) ||
          expression.dynamic_field != null,
      );
      // TODO(marcos): failing the above filter causes the node's condition to be removed
      // but doesn't cause the node to be removed. presumably this is to allow bogus
      // conditions to be filtered, but we should do one more pass after this to verify
      // that the graph is in a good state before allowing it to build because this can
      // result in an empty uneditable condition_ruleset
      if (filteredExpressions.length < expressions.length) {
        // @ts-expect-error - TS18046 - 'node' is of type 'unknown'.
        nodes.set(node.id, {
          // @ts-expect-error - TS2698 - Spread types may only be created from object types.
          ...node,
          condition_ruleset: {
            // @ts-expect-error - TS18046 - 'node' is of type 'unknown'.
            ...node.condition_ruleset,
            expressions: filteredExpressions,
          },
        });
      }
    }
  }

  return {
    ...graph,
    // @ts-expect-error - TS2322 - Type 'Map<unknown, { id: unknown; }>' is not assignable to type 'ConversationNode[] & Map<number, ConversationNode>'.
    nodes,
    // @ts-expect-error - TS2322 - Type 'Map<number, { id: number; }>' is not assignable to type 'Edge[] & Map<number, Edge>'. | TS2345 - Argument of type 'Set<unknown>' is not assignable to parameter of type 'Iterable<{ id: number; }>'.
    edges: fromArray(reachableEdges),
  };
}

export function finalizeGraph(graph: Graph): Graph {
  return {
    ...graph,
    edges: graph.edges.filter(
      (edge) => edge.from_node_id != null && edge.to_node_id,
    ),
  };
}

function clone<T extends Record<any, any>>(
  object: T,
  newProperties: Partial<T>,
): T {
  return {...object, ...newProperties};
}

export function selectGraph(state: ConversationBuilderState): EditableGraph {
  return state.history[state.historyIndex];
}

export function handleHideQuestionSuggestion(
  flowId: Id,
  dispatch: ConversationDispatch,
): void {
  const flowIds =
    window.localStorage.getItem('chatbotHideQuestionSuggestions')?.split(',') ??
    [];
  const newFlowIds = [...flowIds, String(flowId)];
  // @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'string'.
  window.localStorage.setItem('chatbotHideQuestionSuggestions', newFlowIds);

  dispatch({
    type: 'HIDE_QUESTION_SUGGESTION',
    // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'number[]'.
    payload: newFlowIds,
  });
}
